"use client";

import { useState, type ReactNode } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

export interface SheetMetalTrustedApplicationsProps {
	/** 六张行业卡片图（顺序与 industry1～6 文案一致） */
	images: readonly string[];
	learnMoreHref: string;
	translationNamespace?: string;
}

const INDUSTRY_KEYS = ["industry1", "industry2", "industry3", "industry4", "industry5", "industry6"] as const;
const INDUSTRY_HREFS = [
	"/industry/aerospace",
	"/industry/automotive",
	"/industry/industry-equipment",
	"/industry/consumer-electronics",
	"/industry/robotics-automation",
	"/industry/consumer-goods",
] as const;
const PARAGRAPH_KEYS = ["paragraph1"] as const;
const BULLET_KEYS = ["bullet1", "bullet2", "bullet3", "bullet4", "bullet5", "bullet6", "bullet7"] as const;

function CheckRow({ children }: { children: ReactNode }) {
	return (
		<li className="flex items-start gap-3 text-sm text-black md:text-base">
			<span
				className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8bc34a] text-white"
				aria-hidden
			>
				<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M1 5L4.5 8.5L11 1.5"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</span>
			<span className="min-w-0 leading-relaxed">{children}</span>
		</li>
	);
}

function renderBoldPrefix(text: string) {
	const colonIndex = text.indexOf(":");
	if (colonIndex === -1) return text;

	return (
		<>
			<strong>{text.slice(0, colonIndex + 1)}</strong>
			{text.slice(colonIndex + 1)}
		</>
	);
}

/**
 * 钣金：左侧信任文案 + 右侧 3×2 行业图卡 + 底部双列要点（稿图浅绿底、强调色 #8bc34a）
 */
export default function SheetMetalTrustedApplications({
	images,
	translationNamespace = "sheetMetalFabricationTrustedApplications",
}: SheetMetalTrustedApplicationsProps) {
	const t = useTranslations(translationNamespace);
	const [hoveredCard, setHoveredCard] = useState<number | null>(null);
	/** 稿图右下角卡为激活态；悬停时切换高亮 */
	const activeIndex = hoveredCard ?? 5;

	return (
		<section className="bg-[#F6FFDE] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
					{/* 左：标题（末行绿下划线）+ 正文 + Learn More */}
					<div className="min-w-0 text-left">
						<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							<span className="block">{t("titleLine1")}</span>
							<span className="mt-1 inline-flex min-w-0 items-end gap-3 sm:mt-1.5 sm:gap-4">
								<span className="min-w-0 break-words">{t("titleLine2")}</span>
								<span
									className="mb-[0.18em] h-[3px] w-12 shrink-0 bg-[#8bc34a] sm:h-1 sm:w-16 md:w-20"
									aria-hidden
								/>
							</span>
						</h2>
						<div className="space-y-4 text-sm leading-relaxed text-black md:text-base">
							{PARAGRAPH_KEYS.map((key) => (
								<p key={key}>{t(key)}</p>
							))}
						</div>
					</div>

					{/* 右：3×2 行业卡片 */}
					<div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:gap-4">
						{INDUSTRY_KEYS.map((key, i) => {
							const src = images[i];
							const href = INDUSTRY_HREFS[i];
							if (!src) return null;
							const isActive = activeIndex === i;
							return (
								<Link
									key={key}
									href={href}
									className={`group flex min-h-[132px] flex-col justify-between rounded-xl border border-transparent p-3 text-left shadow-sm transition-colors sm:min-h-[128px] sm:p-4 md:min-h-[140px] ${
										isActive
											? "bg-[#8bc34a] text-white"
											: "bg-[#EDEDED] text-black hover:bg-[#8bc34a] hover:text-white"
									}`}
									onMouseEnter={() => setHoveredCard(i)}
									onMouseLeave={() => setHoveredCard(null)}
								>
									<div className="relative mx-0 h-16 w-16 shrink-0 overflow-hidden rounded-full bg-white/80 sm:h-20 sm:w-20 md:h-20 md:w-20">
										<Image
											src={src}
											alt={t(`${key}ImageAlt`)}
											fill
											className="object-cover"
											sizes="(max-width: 640px) 25vw, (max-width: 768px) 96px, 112px"
										/>
									</div>
									<p
										className={`mt-1 self-end text-right text-xs font-bold sm:text-sm ${
											isActive ? "text-white" : "text-gray-900 group-hover:text-white"
										}`}
									>
										{t(`${key}Label`)}
									</p>
								</Link>
							);
						})}
					</div>
				</div>

				{/* 底部：双列要点 */}
				<ul className="mt-12 grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2 md:mt-16 md:gap-y-5 lg:gap-x-16">
					{BULLET_KEYS.map((bk) => (
						<CheckRow key={bk}>{renderBoldPrefix(t(bk))}</CheckRow>
					))}
				</ul>
			</div>
		</section>
	);
}
